<template>
	<div
		data-qa-multisuggest
		:tabindex="searchable ? -1 : tabindex"
		:class="{
			'multiselect--active': isOpen,
			'multiselect--disabled': disabled,
			'multiselect--above': isAbove
		}"
		:state="state"
		class="multiselect"
		role="combobox"
		:aria-owns="'listbox-' + id"
		@focus="activate()"
		@blur="searchable ? false : deactivate()"
		@keydown.self.down.prevent="pointerForward()"
		@keydown.self.up.prevent="pointerBackward()"
		@keypress.enter.tab.stop.self="addPointerElement($event)"
		@keyup.esc="deactivate()"
	>
		<slot v-if="options.length > 0" name="caret" :toggle="toggle">
			<div
				data-qa-multisuggest-caret
				class="multiselect__select"
				@mousedown.prevent.stop="toggle()"
			/>
		</slot>

		<!-- type is used from flow (i.e. custom prop) -->
		<div ref="tags" class="multiselect__tags" :type="type">
			<input
				:id="id"
				ref="search"
				data-qa-multisuggest-input
				:name="name"
				type="text"
				multisuggest="off"
				spellcheck="false"
				:placeholder="placeholder"
				:value="value"
				:disabled="disabled"
				:tabindex="tabindex"
				class="multiselect__input"
				:aria-controls="'listbox-' + id"
				@input="$emit('input', $event.target.value) && updateSearch($event.target.value)"
				@focus.prevent="activate()"
				@blur.prevent="deactivate()"
				@keyup.esc="deactivate()"
				@keydown.down.prevent="pointerForward()"
				@keydown.up.prevent="pointerBackward()"
				@keypress.enter.prevent.stop.self="addPointerElement($event)"
				@keydown.delete.stop="removeLastElement()"
			/>
		</div>

		<transition name="multiselect">
			<div
				v-show="isOpen && !isEmpty"
				ref="list"
				class="multiselect__content-wrapper flow-add-scrollbar"
				tabindex="-1"
				:style="{
					maxHeight: optimizedHeight + 'px',
					width: optionsWidth,
					top: optionsTop
				}"
				@focus="activate"
				@mousedown.prevent
			>
				<ul :id="'listbox-' + id" class="multiselect__content" :style="contentStyle" role="listbox">
					<slot name="beforeList" />

					<li
						v-for="(option, index) of filteredOptions"
						:id="id + '-' + index"
						:key="index"
						:data-header="option.header && !headerInOption ? option.header : null"
						class="multiselect__element"
						:class="{
							'last-child': index + 1 === filteredOptions.length,
							'border-bottom': option.bottomBorder
						}"
						:role="!(option && (option.$isLabel || option.$isDisabled)) ? 'option' : null"
					>
						<span
							:class="optionHighlight(index, option)"
							:data-select="selectLabelText"
							:data-selected="selectedLabelText"
							:data-deselect="deselectLabelText"
							class="multiselect__option"
							:data-qa-multisuggest-option="getOptionLabel(option)"
							@click.stop="select(option)"
							@mouseenter.self="pointerSet(index)"
						>
							<slot name="option" :option="option" :search="search" :index="index">
								<span>{{ getOptionLabel(option) }}</span>
							</slot>
						</span>
					</li>

					<slot name="afterList" />
				</ul>
			</div>
		</transition>
	</div>
</template>
<script>
import { defineComponent } from "vue";
import Multiselect from "vue-multiselect";

export default defineComponent({
	name: "Multisuggest",

	mixins: [Multiselect],

	props: {
		/**
		 * 'default' | 'error' | 'success' | 'warning'
		 */
		state: {
			type: String,
			default: "default"
		},
		type: {
			type: String,
			default: "curved",
			validator(value) {
				return ["curved", "rounded"].includes(value);
			}
		},

		/**
		 * Override the default props set by vue-multiselect to act more like an autosuggest
		 */
		clearOnSelect: {
			type: Boolean,
			default: false
		},
		preserveSearch: {
			type: Boolean,
			default: true
		},
		selectLabel: {
			type: String,
			default: ""
		}
	},

	data() {
		return {
			optionsWidth: "50px",
			optionsTop: "0px"
		};
	},

	computed: {
		isEmpty() {
			return this.filteredOptions.length === 0 && !this.loading;
		},

		filteredOptions() {
			const normalizedSearch = (this.search || "").toLocaleLowerCase().trim();

			if (normalizedSearch === "") {
				return this.options;
			}

			const options = this.options.filter(option =>
				option.toLocaleLowerCase().includes(normalizedSearch)
			);

			// multisuggest is a helper not a strict filter, so if nothing is left we show all options
			if (options.length === 0) {
				return this.options;
			}

			return options;
		}
	},

	mounted() {
		window.addEventListener("scroll", this.handleWindowScroll, {
			capture: true
		});
		this.updateDimentions();
	},

	beforeDestroy() {
		window.removeEventListener("scroll", this.handleWindowScroll, {
			capture: true
		});
	},

	updated() {
		this.updateDimentions();
	},

	methods: {
		updateDimentions() {
			this.optionsWidth = `${this.$el.offsetWidth}px`;

			const spaceAbove = this.$el.getBoundingClientRect().top;
			const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
			const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;

			const optionsHeight = this.$refs.list.offsetHeight;
			const heightToApply = Math.min(optionsHeight, this.maxHeight);
			if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
				this.preferredOpenDirection = "below";
				this.optimizedHeight = +Math.min(spaceBelow - 40, this.maxHeight).toFixed(0);
				this.optionsTop = `${(this.$el.getBoundingClientRect().top + this.$el.offsetHeight).toFixed(
					0
				)}px`;
			} else {
				this.preferredOpenDirection = "above";
				this.optimizedHeight = +Math.min(spaceAbove - 40, this.maxHeight).toFixed(0);

				this.optionsTop = `${(this.$el.getBoundingClientRect().top - heightToApply).toFixed(0)}px`;
			}
		},

		handleWindowScroll() {
			if (this.isOpen) {
				this.updateDimentions();
			}
		}
	}
});
</script>
